import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Testimonial {
  id: string;
  brand_id: string;
  name: string;
  title?: string;
  testimonial: string;
  image_url?: string;
  rating?: number;
  company?: string;
  website?: string;
  is_published: boolean;
  display_order: number;
  source: 'manual' | 'webhook' | 'public_form';
  created_at: string;
  updated_at: string;
}

export function useTestimonials(brandId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: testimonials, isLoading } = useQuery({
    queryKey: ['testimonials', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('brand_testimonials')
        .select('*')
        .eq('brand_id', brandId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Testimonial[];
    },
    enabled: !!brandId,
  });

  const createMutation = useMutation({
    mutationFn: async (testimonial: any) => {
      const { data, error } = await supabase
        .from('brand_testimonials')
        .insert([testimonial])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials', brandId] });
      toast.success('Testimonial created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create testimonial: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Testimonial> & { id: string }) => {
      const { data, error } = await supabase
        .from('brand_testimonials')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials', brandId] });
      toast.success('Testimonial updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update testimonial: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('brand_testimonials')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials', brandId] });
      toast.success('Testimonial deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete testimonial: ${error.message}`);
    },
  });

  return {
    testimonials: testimonials || [],
    isLoading,
    createTestimonial: createMutation.mutateAsync,
    updateTestimonial: updateMutation.mutateAsync,
    deleteTestimonial: deleteMutation.mutateAsync,
  };
}
